import { useEffect, useRef } from "react";

import {
  isDismissKey,
  shouldDismissOnFocusOut,
  shouldDismissOnPointer,
  shouldDismissOnRoute,
} from "@/lib/dismiss";

/**
 * Close a transient overlay on every interaction that means "I'm done with
 * this" — a press outside, focus leaving, Esc, or a route change.
 *
 * Shell-wide by design: search is the first caller, but any panel, popover or
 * menu the shell grows should take its dismissal from here rather than
 * re-deciding it. The decisions themselves live in `lib/dismiss.ts` and are
 * unit-tested; this hook is the wiring, deliberately thin.
 *
 * `pointerdown` rather than `click` so the panel closes on the press, matching
 * every other menu on the platform — safe only because the handler checks
 * containment first (see `shouldDismissOnPointer`).
 *
 * @param ref       the element that counts as "inside" — must wrap the trigger
 *                  AND the panel, or tabbing from one to the other closes it.
 * @param open      whether the overlay is currently showing; nothing is bound
 *                  while it is closed.
 * @param onDismiss called at most once per interaction.
 * @param pathname  the current route. Pass it and a navigation closes the
 *                  overlay; omit it and route changes are ignored.
 */
export function useDismissable(
  ref: { current: HTMLElement | null },
  open: boolean,
  onDismiss: () => void,
  pathname?: string,
): void {
  // Held in a ref so the effect below does not re-bind its listeners on every
  // render just because the caller passed a fresh closure.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  // The route the overlay was opened at, captured on open.
  const openedAtRef = useRef<string | null>(null);
  if (!open) openedAtRef.current = null;
  else if (openedAtRef.current === null) openedAtRef.current = pathname ?? null;

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (shouldDismissOnPointer(ref.current, event.target)) dismissRef.current();
    };
    const onFocusOut = (event: FocusEvent) => {
      if (shouldDismissOnFocusOut(ref.current, event.relatedTarget)) dismissRef.current();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (isDismissKey(event)) dismissRef.current();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, ref]);

  useEffect(() => {
    if (!open || pathname === undefined) return;
    if (shouldDismissOnRoute(openedAtRef.current, pathname)) dismissRef.current();
  }, [open, pathname]);
}
